import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // Import the translation hook

interface UserDialogProps {
    isOpen: boolean;
    isEditModal: boolean;
    name: string;
    email: string;
    role: string;
    phone_number: string;
    notify_user: boolean;
    setName: (value: string) => void;
    setEmail: (value: string) => void;
    setRole: (value: string) => void;
    setPhoneNumber: (value: string) => void;
    setNotifyUser: (value: boolean) => void;
    handleSubmit: (e: React.FormEvent) => void;
    closeDialog: () => void;
}

const UserDialog: React.FC<UserDialogProps> = ({
    isOpen,
    isEditModal,
    name,
    email,
    role,
    phone_number,
    notify_user,
    setName,
    setEmail,
    setRole,
    setPhoneNumber,
    setNotifyUser,
    handleSubmit,
    closeDialog,
}) => {
    const [t] = useTranslation('teams');

    useEffect(() => {
        if (!phone_number) {
            setNotifyUser(false);
        }
    }, [phone_number]);

    const backdropStyle = {
        display: isOpen ? 'block' : 'none',
        position: 'fixed' as 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
    };

    return (
        <div style={backdropStyle}>
            <dialog
                open={isOpen}
                className="modal"
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        closeDialog();
                    }
                }}
            >
                <div className="modal-box flex flex-col gap-4 p-6 rounded-lg bg-white shadow-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold">{isEditModal ? t('edit_person') : t('add_person')}</h2>
                        <button
                            onClick={closeDialog}
                            className="text-gray-500 hover:text-gray-700"
                            aria-label={t('close')}
                        >
                            ✕
                        </button>
                    </div>
                    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                        <div className="flex flex-col">
                            <label htmlFor="name" className="text-sm font-medium text-gray-600">
                                {t('name')}
                            </label>
                            <input
                                id="name"
                                type="text"
                                placeholder={t('enter_name')}
                                className="input border border-gray-300 rounded-md p-2 text-sm text-gray-700"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="flex flex-col">
                            <label htmlFor="email" className="text-sm font-medium text-gray-600">
                                {t('email')}
                            </label>
                            <input
                                id="email"
                                type="email"
                                placeholder={t('enter_email')}
                                className={`input border border-gray-300 rounded-md p-2 text-sm text-gray-700 ${isEditModal ? 'bg-white' : ''}`}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                                title={t('enter_valid_email')}
                                readOnly={isEditModal}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label htmlFor="role" className="text-sm font-medium text-gray-600">
                                {t('role')}
                            </label>
                            <select
                                id="role"
                                className="input border rounded-md p-2 text-sm text-gray-700 border-gray-300"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                required
                            >
                                <option value="">{t('select_role')}</option>
                                <option value="ADMIN">{t('role_admin')}</option>
                                <option value="EDITOR">{t('role_editor')}</option>
                                <option value="COLLABORATOR">{t('role_collaborator')}</option>
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <label htmlFor="phone_number" className="text-sm font-medium text-gray-600">
                                {t('phone_number')}
                            </label>
                            <input
                                id="phone_number"
                                type="number"
                                placeholder={t('enter_phone_number')}
                                className="input border border-gray-300 rounded-md p-2 text-sm text-gray-700"
                                value={phone_number}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                onInput={(e) => {
                                    const input = e.target as HTMLInputElement;
                                    if (input.value.length > 0 && input.value.length < 9) {
                                        input.setCustomValidity(t('phone_number_validation'));
                                    } else {
                                        input.setCustomValidity('');
                                    }
                                }}
                                title={t('phone_number_validation')}
                            />
                            <small className="text-gray-500">{t('phone_number_instructions')}</small>
                        </div>
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                                <input
                                    id="notify_user"
                                    type="checkbox"
                                    checked={notify_user}
                                    onChange={(e) => {
                                        if (!phone_number) {
                                            setNotifyUser(false);
                                        } else {
                                            setNotifyUser(e.target.checked);
                                        }
                                    }}
                                />
                                <label htmlFor="notify_user" className="text-sm font-medium text-gray-600">
                                    {t('notify_user')}
                                </label>
                            </div>
                            {!phone_number && (
                                <small className="text-red-500">{t('phone_number_required_for_notify')}</small>
                            )}
                        </div>
                        <div className="flex justify-between items-center mt-4">
                            <button
                                type="button"
                                onClick={closeDialog}
                                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 rounded-md bg-pink-500 text-white font-semibold hover:bg-pink-600"
                            >
                                {isEditModal ? t('edit') : t('add')}
                            </button>
                        </div>
                    </form>
                </div>
            </dialog>
        </div>
    );
};

export default UserDialog;